import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { memberInfoKey } from '@/hooks/member/memberInfoApi';
import { ProfilePhotoUploadError, putProfilePhoto } from '@/lib/profile/photoUpload';
import { useRequestProfilePhotoUploadUrl } from './useRequestProfilePhotoUploadUrl';

/**
 * Saves the member's profile photo: mints a write SAS URL, PUTs the composited PNG to it, then
 * invalidates the shared `myMemberInfo` cache so the refreshed photo shows up (`useMyProfile`'s
 * `refetchOnMount: 'always'` fetches a fresh SAS read URL on the next mount, rather than the
 * spent write URL this mutation just used).
 *
 * Returns `mutateAsync` (via the standard `UseMutationResult`) rather than only settling into
 * `isError` — `UpdateProfilePhotoDialog`'s `handleSave` (CNP-714) awaits `onSave` and only closes
 * the dialog when it resolves, leaving the dialog open on the position step for a retry when it
 * rejects. A version that never rejects would close the dialog on every failed save, right after
 * showing the error toast below.
 *
 * Two failure sources, told apart by error type rather than by which step ran: `putProfilePhoto`
 * is the only thing that throws `ProfilePhotoUploadError` (a raw PUT to Blob Storage, not
 * GraphQL, so there's no error code to translate — the "saveFailed" copy is its own
 * members-namespace string). Anything else came from `createProfilePhotoUploadUrl` itself and
 * goes through the usual `extractGqlErrorCode` + shared `errors` namespace path.
 */
export function useSaveProfilePhoto(): UseMutationResult<void, Error, Blob> {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const { t } = useTranslation('members');
  const queryClient = useQueryClient();
  const requestUploadUrl = useRequestProfilePhotoUploadUrl();

  return useMutation<void, Error, Blob>({
    mutationFn: async (photo: Blob) => {
      const { url } = await requestUploadUrl.mutateAsync();
      await putProfilePhoto(url, photo);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberInfoKey });
    },
    onError: (error: unknown) => {
      if (error instanceof ProfilePhotoUploadError) {
        // `error.message` never contains the SAS URL itself (see photoUpload.ts) — safe to log.
        trackException(new Error(`profile photo upload failed: ${error.message}`), {
          source: 'profilePhotoUpload',
        });
        toast({ title: tError('title'), description: t('profile.photo.errors.saveFailed'), variant: 'destructive' });
        return;
      }
      const code = extractGqlErrorCode(error);
      trackException(new Error(`createProfilePhotoUploadUrl failed: ${code}`), {
        source: 'createProfilePhotoUploadUrl',
        errorCode: code,
      });
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });
}
